/**
 * Properties panel (docs/06-UI-SPEC.md §6.3), 240 px on the right.
 *
 * Contextual to the active tool: the eraser has no colour and no ink thickness, so
 * neither is shown while it is in hand.
 */

import type { ToolId } from '@opal/shared';

import { BackgroundPicker } from './BackgroundPicker.js';
import { ColorPalette } from './ColorPalette.js';
import { EraserControls } from './EraserControls.js';
import { SelectControls } from './SelectControls.js';
import { ShapeControls } from './ShapeControls.js';
import { SmartPenControls } from './SmartPenControls.js';
import { LaserNote } from './LaserNote.js';
import { LayerPicker } from './LayerPicker.js';
import { StickyControls } from './StickyControls.js';
import { TextControls } from './TextControls.js';
import { ThicknessSlider } from './ThicknessSlider.js';
import { UndoRedo } from './UndoRedo.js';
import { ToolPicker } from './ToolPicker.js';
import { useBoardStore } from '../../board/store.js';

const TITLES: Readonly<Record<ToolId, string>> = {
  pen: 'Pen',
  highlighter: 'Highlighter',
  eraser: 'Eraser',
  select: 'Select',
  shape: 'Shapes',
  text: 'Text',
  sticky: 'Sticky note',
  laser: 'Laser pointer',
};

/** The tools that draw in ink, and so choose a colour from the same palette. */
const INKED: readonly ToolId[] = ['pen', 'highlighter', 'shape', 'text'];

/** The tools whose mark has a thickness. Writing has a size instead. */
const THICK: readonly ToolId[] = ['pen', 'highlighter', 'shape'];

export interface PropertiesPanelProps {
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  /** SRS C8 — the teacher-only layer is the teacher's, so only she is offered it. */
  readonly showLayers: boolean;
  /** The paper is part of the page, so it is the teacher's to change (SRS D4). */
  readonly showPaper: boolean;
}

export function PropertiesPanel({ onUndo, onRedo, showPaper, showLayers }: PropertiesPanelProps) {
  const tool = useBoardStore((state) => state.tool);

  return (
    <aside
      // Reaching for a control in here does not finish what is being typed: the box
      // stays open so the words can be made bold, bigger or a different colour.
      data-keeps-writing
      className="flex h-full w-panel shrink-0 flex-col gap-6 overflow-y-auto border-l border-border bg-surface p-4"
    >
      <h2 className="text-base font-semibold text-text-primary">{TITLES[tool]}</h2>
      <UndoRedo onUndo={onUndo} onRedo={onRedo} />
      <ToolPicker />
      {showLayers ? <LayerPicker /> : null}
      {tool === 'eraser' ? <EraserControls /> : null}
      {tool === 'select' ? <SelectControls /> : null}
      {tool === 'shape' ? <ShapeControls /> : null}
      {tool === 'text' ? <TextControls /> : null}
      {tool === 'sticky' ? <StickyControls /> : null}
      {tool === 'laser' ? <LaserNote /> : null}
      {INKED.includes(tool) ? <ColorPalette /> : null}
      {THICK.includes(tool) ? <ThicknessSlider /> : null}
      {/* SRS C5 — offered with the pen, which is the only tool it acts on. */}
      {tool === 'pen' ? <SmartPenControls /> : null}
      {showPaper ? <BackgroundPicker /> : null}
    </aside>
  );
}
